import {
	ChangeDetectionStrategy,
	Component,
	CUSTOM_ELEMENTS_SCHEMA,
	effect,
	inject,
	input,
	output,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DestroyService } from '@context/frontend/common';
import { getUaDevice } from '@context/frontend/native/platform';
import {
	PageSize,
	PageSizes,
	PaginatedPageDetails,
} from '@context/shared/types/pagination';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiDataList } from '@taiga-ui/core';
import {
	TuiChevron,
	TuiDataListWrapper,
	TuiPagination,
	TuiSelect,
} from '@taiga-ui/kit';
import { takeUntil } from 'rxjs';
import {
	PlaceholderConfig,
	TableLoaderComponent,
	TablePlaceholderComponent,
} from '../';

@Component({
	imports: [
		TableLoaderComponent,
		TablePlaceholderComponent,
		TranslocoModule,
		TuiPagination,
		TuiDataList,
		TuiDataListWrapper,
		TuiSelect,
		TuiChevron,
		FormsModule,
		ReactiveFormsModule,
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	selector: 'ctx-table',
	templateUrl: 'table.component.html',
	styleUrl: 'table.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [DestroyService],
	host: {
		'[attr.device]': 'device',
		'[attr.disabled]': 'disabled()',
		'[attr.paginated]': 'pagination()',
	},
})
export class TableComponent {
	readonly device = getUaDevice();

	readonly disabled = input(false);

	readonly loading = input(false);

	readonly placeholder = input.required<PlaceholderConfig>();

	readonly empty = input(false);

	readonly index = input(0);

	readonly length = input(0);

	readonly details = input<PaginatedPageDetails | null>(null);

	readonly pagination = input(false);

	readonly size = input<PageSize>(10);

	readonly loadMore = output<void>();

	readonly indexChange = output<number>();

	readonly sizeChange = output<PageSize>();

	readonly destroy$ = inject(DestroyService);
	readonly sizes = PageSizes;
	readonly sizeControl = new FormControl(10 as PageSize);

	constructor() {
		// Keep the control in sync with the `size` input without re-emitting
		// (emitEvent: false avoids a feedback loop back into sizeChange).
		effect(() => {
			this.sizeControl.setValue(this.size(), { emitEvent: false });
		});

		// Subscribe once for the lifetime of the component. Previously this lived
		// inside an effect, so every `size` change stacked another subscription
		// and fired duplicate sizeChange events (extra fetches on every paging).
		this.sizeControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((value) => {
				if (value && value !== this.size()) {
					this.sizeChange.emit(value);
				}
			});
	}

	onIndexChange(index: number) {
		this.indexChange.emit(index);
	}
}
